// The route tree. Signed-out pages live under /auth; everything else needs a session (RequireAuth), and
// organisation pages sit under /o/:orgId (OrgLayout checks the membership). The chart (uPlot), QR code
// and settings pages load on demand.

import { createBrowserRouter, isRouteErrorResponse, Link, Navigate, useRouteError } from 'react-router';
import { FullPage, OrgLayout, PlainLayout, RequireAuth } from './components/Shell.tsx';
import { Alert, Button } from './components/ui.tsx';
import { useI18n } from './i18n/index.ts';
import { Alarms, AlarmDetail } from './pages/Alarms.tsx';
import {
  AcceptInvitation, AuthLayout, ForgotPassword, Impersonate, MagicLink, ResetPassword, SignIn, SignUp, SupportEnded,
  VerifyEmail,
} from './pages/auth/AuthPages.tsx';
import { ClaimDevice, DeviceDetail, Devices } from './pages/Devices.tsx';
import { AlarmLink, Home } from './pages/Home.tsx';
import { KioskOpen } from './pages/Kiosk.tsx';
import { Onboarding } from './pages/Onboarding.tsx';
import { Overview } from './pages/Overview.tsx';

const settings = () => import('./pages/settings/index.ts');
const haccp = () => import('./pages/haccp/index.ts');

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          { index: true, element: <Navigate to="sign-in" replace /> },
          { path: 'sign-in', element: <SignIn /> },
          { path: 'sign-up', element: <SignUp /> },
          { path: 'verify-email', element: <VerifyEmail /> },
          { path: 'magic-link', element: <MagicLink /> },
          { path: 'forgot-password', element: <ForgotPassword /> },
          { path: 'reset-password', element: <ResetPassword /> },
          { path: 'impersonate', element: <Impersonate /> },
          { path: 'support-ended', element: <SupportEnded /> },
        ],
      },
      { path: 'k/:token?', element: <KioskOpen /> },
      { path: 'invitations/accept', element: <AuthLayout />, children: [{ index: true, element: <AcceptInvitation /> }] },
      {
        element: <RequireAuth />,
        children: [
          { index: true, element: <Home /> },
          { path: 'alarms/:alarmId', element: <AlarmLink /> },
          {
            element: <PlainLayout />,
            children: [
              { path: 'onboarding', element: <Onboarding /> },
              { path: 'profile', lazy: () => import('./pages/Profile.tsx').then((m) => ({ Component: m.Profile })) },
            ],
          },
          {
            path: 'o/:orgId',
            element: <OrgLayout />,
            children: [
              { index: true, element: <Overview /> },
              { path: 'assets/:assetId', lazy: () => import('./pages/AssetDetail.tsx').then((m) => ({ Component: m.AssetDetail })) },
              { path: 'alarms', element: <Alarms /> },
              { path: 'alarms/:alarmId', element: <AlarmDetail /> },
              { path: 'devices', element: <Devices /> },
              { path: 'devices/claim', element: <ClaimDevice /> },
              { path: 'devices/:deviceId', element: <DeviceDetail /> },
              { path: 'setup', lazy: () => import('./pages/Setup.tsx').then((m) => ({ Component: m.SetupAssistant })) },
              { path: 'lora', lazy: () => import('./pages/Lora.tsx').then((m) => ({ Component: m.LoraBases })) },
              { path: 'lora/new', lazy: () => import('./pages/Lora.tsx').then((m) => ({ Component: m.LoraOnboarding })) },
              { path: 'lora/:deviceId', lazy: () => import('./pages/Lora.tsx').then((m) => ({ Component: m.LoraBaseDetail })) },
              { path: 'energy', lazy: () => import('./pages/Energy.tsx').then((m) => ({ Component: m.EnergyFleet })) },
              // The read-only dashboard's exports (lib/kiosk.ts): the settings page, outside Settings.
              { path: 'exports', lazy: () => settings().then((m) => ({ Component: m.ExportsSettings })) },
              { path: 'maintenance', lazy: () => import('./pages/Maintenance.tsx').then((m) => ({ Component: m.MaintenanceFleet })) },
              {
                // HACCP records: one tab per module (pages/haccp/index.ts, HACCP_TABS).
                path: 'haccp',
                lazy: () => haccp().then((m) => ({ Component: m.HaccpLayout })),
                children: [
                  { index: true, lazy: () => haccp().then((m) => ({ Component: m.HaccpHome })) },
                  { path: 'cooling', lazy: () => haccp().then((m) => ({ Component: m.CoolingTab })) },
                  { path: 'cooling/:cycleId', lazy: () => haccp().then((m) => ({ Component: m.CoolingCycleDetail })) },
                  { path: 'reception', lazy: () => haccp().then((m) => ({ Component: m.ReceptionTab })) },
                  { path: 'reception/:receptionId', lazy: () => haccp().then((m) => ({ Component: m.ReceptionDetail })) },
                  { path: 'cleaning', lazy: () => haccp().then((m) => ({ Component: m.CleaningTab })) },
                ],
              },
              {
                path: 'settings',
                lazy: () => settings().then((m) => ({ Component: m.SettingsLayout })),
                children: [
                  { index: true, element: <Navigate to="organisation" replace /> },
                  { path: 'organisation', lazy: () => settings().then((m) => ({ Component: m.OrganisationSettings })) },
                  { path: 'members', lazy: () => settings().then((m) => ({ Component: m.MembersSettings })) },
                  { path: 'sites', lazy: () => settings().then((m) => ({ Component: m.SitesSettings })) },
                  { path: 'rules', lazy: () => settings().then((m) => ({ Component: m.RulesSettings })) },
                  { path: 'escalation', lazy: () => settings().then((m) => ({ Component: m.EscalationSettings })) },
                  { path: 'notifications', lazy: () => settings().then((m) => ({ Component: m.NotificationSettings })) },
                  { path: 'exports', lazy: () => settings().then((m) => ({ Component: m.ExportsSettings })) },
                  { path: 'audit', lazy: () => settings().then((m) => ({ Component: m.AuditSettings })) },
                  { path: 'billing', lazy: () => settings().then((m) => ({ Component: m.BillingSettings })) },
                  { path: 'haccp', lazy: () => settings().then((m) => ({ Component: m.HaccpSettings })) },
                  { path: 'kiosks', lazy: () => settings().then((m) => ({ Component: m.KioskSettings })) },
                ],
              },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
      { path: '*', element: <FullPage><NotFound /></FullPage> },
    ],
  },
]);

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="mx-auto max-w-md py-10 text-center">
      <p className="text-4xl font-semibold text-slate-300">404</p>
      <p className="mt-2 font-medium text-slate-800">{t('errors.notFound')}</p>
      <Link to="/" className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline">{t('errors.home')}</Link>
    </div>
  );
}

/** Rendering errors, and chunks that no longer exist after a deployment (a reload fetches the new ones). */
function RouteError() {
  const error = useRouteError();
  const { t } = useI18n();
  if (isRouteErrorResponse(error) && error.status === 404) return <FullPage><NotFound /></FullPage>;
  const stale = error instanceof TypeError && /dynamically imported module|Importing a module script failed/i.test(error.message);
  if (!stale) console.error(error);
  return (
    <FullPage>
      <div className="flex max-w-md flex-col items-center gap-4">
        <Alert tone={stale ? 'blue' : 'red'} title={stale ? t('errors.newVersion') : t('errors.crashed')} />
        <Button onClick={() => window.location.reload()}>{t('errors.reload')}</Button>
      </div>
    </FullPage>
  );
}
